import React, { ComponentPropsWithoutRef } from "react"
import { useField, UseFieldConfig } from "react-final-form"
import { useId } from "@reach/auto-id"
import clsx from "clsx"
import CardElement from "./CardElement"

export interface LabeledCreditCardFieldProps
  extends Omit<ComponentPropsWithoutRef<typeof CardElement>, "onChange"> {
  /** Field name. */
  name: string
  /** Field label. */
  label: string
  outerProps?: ComponentPropsWithoutRef<"div">
  fieldProps?: UseFieldConfig<string>
  hiddenLabel?: boolean
}

export const LabeledCreditCardField = React.forwardRef<
  HTMLInputElement,
  LabeledCreditCardFieldProps
>(({ name, label, outerProps, fieldProps, hiddenLabel, className, ...props }, _ref) => {
  const {
    input,
    meta: { touched, error, submitError, submitting },
  } = useField(name, fieldProps)
  const id = useId() + "name"

  const normalizedError = Array.isArray(error) ? error.join(", ") : error || submitError
  const showError = touched && normalizedError

  return (
    <div {...outerProps}>
      <div className="flex justify-between text-sm font-medium leading-5">
        <label htmlFor={id} className={clsx("text-gray-700", hiddenLabel && "sr-only")}>
          {label}
        </label>
        <div role="alert" className="ml-1 text-xs text-right text-red-700">
          {showError && normalizedError}
        </div>
      </div>
      <div className="mt-1 rounded-md shadow-sm">
        <CardElement
          id={id}
          disabled={submitting}
          {...props}
          onBlur={input.onBlur}
          onChange={input.onChange}
          className={clsx(
            "bg-white block w-full px-3 py-2 placeholder-gray-400 border rounded-md sm:text-sm sm:leading-5 can-disable",
            showError ? "border-red-500" : "border-gray-300",
            className
          )}
        />
      </div>
    </div>
  )
})

export default LabeledCreditCardField
